import { Button } from "@/components/ui/button.tsx";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select.tsx";
import { cn } from "@/lib/utils.ts";
import { formatTime12 } from "./cronUtils.ts";

interface TimeOfDaySelectProps {
  /** Every hour the schedule fires at, in 24-hour form (0–23), sorted. */
  hours: number[];
  /** The minute every one of those hours shares (one of the 5-minute steps). */
  minute: number;
  /** Emits the new hours (sorted, 24-hour) and minute so callers keep their wiring. */
  onChange: (hours: number[], minute: number) => void;
  disabled?: boolean;
}

const MINUTES = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];
const HOURS = Array.from({ length: 24 }, (_, i) => i);

/**
 * The "At" block shared by the schedule editors: one minute select and a chip
 * per hour of the day, so a routine can fire several times a day. Each chip
 * reads as the full time it stands for ("9:30 AM"), with the chosen minute
 * folded in, so the selection reads back exactly as the schedule will run.
 *
 * Several hours share the one minute on purpose. A 5-field cron has a single
 * minute field, so "9:00 and 5:00" is one expression and "9:00 and 5:30" is not;
 * a separate minute per time would promise schedules the string cannot carry.
 * At least one hour stays selected — an empty hour field is not a cron either.
 */
export const TimeOfDaySelect = ({ hours, minute, onChange, disabled }: TimeOfDaySelectProps) => {
  const toggleHour = (hour: number) => {
    const next = hours.includes(hour) ? hours.filter((h) => h !== hour) : [...hours, hour];
    if (next.length === 0) return;
    onChange(
      next.sort((a, b) => a - b),
      minute,
    );
  };

  return (
    // A container, so the chip grid picks its column count from the width it
    // actually has: the editor sits in a narrow canvas card and a wide routine
    // form alike, and a viewport breakpoint would lay both out the same.
    <div className="@container space-y-1">
      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground w-16 shrink-0">At</span>
        <Select
          value={String(minute)}
          disabled={disabled}
          onValueChange={(m) => onChange(hours, Number(m))}
        >
          <SelectTrigger className="h-7 text-xs w-20 rounded-md" aria-label="Minute">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {MINUTES.map((m) => (
              <SelectItem key={m} value={String(m)}>
                :{String(m).padStart(2, "0")}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-xs text-muted-foreground">for each time</span>
      </div>
      <div className="flex items-start gap-2">
        <span className="text-xs text-muted-foreground w-16 shrink-0" />
        {/* Column counts that divide 24, so a wrap always leaves even lines. */}
        <div className="grid flex-1 grid-cols-4 gap-1 @md:grid-cols-6 @2xl:grid-cols-8">
          {HOURS.map((hour) => {
            const selected = hours.includes(hour);
            const label = formatTime12(hour, minute);
            return (
              <Button
                key={hour}
                variant="blank"
                size="fit"
                disabled={disabled}
                aria-pressed={selected}
                onClick={() => toggleHour(hour)}
                data-statsig-name="cron-schedule-hour"
                className={cn(
                  "h-7 rounded-md text-xs font-medium tabular-nums",
                  selected
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground hover:bg-muted/80",
                )}
              >
                {label}
              </Button>
            );
          })}
        </div>
      </div>
    </div>
  );
};
